/**
 * t607u: My Shortcuts, pages you saved from the top bar's bookmark (desktop only). They're listed under Home in the
 * sidebar, in your order. Stored per person per workspace on the server (GET/POST/PATCH/DELETE /nav/shortcuts).
 */
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { normaliseShortcutPath, settingsSectionAvailable, SHORTCUT_LABEL_MAX, type MyPermissions, type UserShortcut, type UserShortcuts } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { usePermissions } from '@/lib/permissions';
import { useNavAccess } from '@/lib/navAccess';
import { allNavItems } from '@/lib/navModel';
import { canSee, SECTIONS } from '@/features/settings/registry';

export const ADD_LABEL = 'Add to My Shortcuts';
export const REMOVE_LABEL = 'Remove from My Shortcuts';
export const SECTION_TITLE = 'My Shortcuts';
/** Between the parts of a default name, e.g. "Settings › Notifications". */
export const SEP = ' › ';

export function useShortcuts(enabled = true) {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: qk.shortcuts(ws ?? ''), queryFn: () => api.shortcuts(ws!), enabled: Boolean(ws) && enabled, staleTime: 60_000 });
}

/** Every change returns the whole list, so the cache is replaced with it (the sidebar and the bookmark update together). */
export function useShortcutActions() {
  const ws = useWorkspaceId();
  const put = (r: UserShortcuts) => { if (ws) queryClient.setQueryData(qk.shortcuts(ws), r); return r; };
  const need = () => { if (!ws) throw new Error('No workspace'); return ws; };
  return {
    add: async (path: string, label: string) => put(await api.addShortcut(need(), { path, label })),
    rename: async (id: string, label: string) => put(await api.renameShortcut(need(), id, label)),
    remove: async (id: string) => put(await api.removeShortcut(need(), id)),
    move: async (list: UserShortcut[], id: string, by: -1 | 1) => {
      const ids = moveId(list.map(s => s.id), id, by);
      if (!ids) return null;
      // Optimistic: the row moves straight away; the server's list replaces it (or the old one comes back on error).
      if (ws) queryClient.setQueryData<UserShortcuts>(qk.shortcuts(ws), { shortcuts: ids.map((i, n) => ({ ...list.find(s => s.id === i)!, position: n })) });
      try { return put(await api.reorderShortcuts(need(), ids)); } catch (e) { if (ws) put({ shortcuts: list }); throw e; }
    },
  };
}

/** The ids with `id` moved one place up (-1) or down (1); null when it can't move. */
export function moveId(ids: string[], id: string, by: -1 | 1): string[] | null {
  const i = ids.indexOf(id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  const out = [...ids];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** The page you're on as a shortcut stores it (path and query, no hash). */
export function useCurrentShortcutPath(): string | null {
  const loc = useLocation();
  return normaliseShortcutPath(`${loc.pathname}${loc.search}`);
}

const settingsParts = (pathname: string) => {
  const m = /^\/settings\/([^/]+)\/([^/]+)/.exec(pathname);
  return m ? { group: m[1], item: m[2] } : null;
};

/**
 * The name a new shortcut starts with: "Settings › <section>" in Settings, else the menu item ("Tasks"), with the
 * page's own title after it on a page inside it ("Projects › Store refit"), else the page title. Always editable.
 */
export function defaultShortcutLabel(path: string, pageTitle?: string | null): string {
  const pathname = path.split('?')[0];
  const title = (pageTitle ?? '').replace(/\s+/g, ' ').trim();
  const cut = (s: string) => s.slice(0, SHORTCUT_LABEL_MAX).trim();
  if (pathname === '/settings') return 'Settings';
  const sp = settingsParts(pathname);
  if (sp) {
    const s = SECTIONS.find(x => x.group === sp.group && x.item === sp.item);
    return cut(s ? `Settings${SEP}${s.title}` : title ? `Settings${SEP}${title}` : 'Settings');
  }
  const items = allNavItems();
  const exact = items.find(i => i.to === pathname);
  if (exact) return cut(exact.label);
  const parent = items.filter(i => i.to !== '/' && pathname.startsWith(`${i.to}/`)).sort((a, b) => b.to.length - a.to.length)[0];
  if (parent) return cut(title && title !== parent.label ? `${parent.label}${SEP}${title}` : parent.label);
  return cut(title || 'This page');
}

/** The page's own heading, read from the main area when the bookmark is pressed (first non-empty h1). */
export function readPageTitle(root: ParentNode | null = typeof document === 'undefined' ? null : document.getElementById('main')): string | null {
  if (!root) return null;
  for (const h of root.querySelectorAll('h1')) {
    const t = h.textContent?.replace(/\s+/g, ' ').trim();
    if (t) return t;
  }
  return null;
}

type Mine = Pick<MyPermissions, 'workspace' | 'scoped' | 'modules_off'> | null | undefined;

/** Whether a saved page would open for you now: the same rules as the menus, route guards and Settings sections. */
export function shortcutOpens(mine: Mine, can: (path: string) => boolean, path: string): boolean {
  const pathname = path.split('?')[0];
  const sp = settingsParts(pathname);
  if (sp) {
    const s = SECTIONS.find(x => x.group === sp.group && x.item === sp.item);
    // Unknown or moved sections: Settings sends you to the new place or says it isn't available.
    if (!s) return true;
    if (!canSee(mine, s) || !settingsSectionAvailable(mine?.modules_off, `${s.group}/${s.item}`)) return false;
    return s.href ? can(s.href.split('?')[0]) : true;
  }
  return can(pathname);
}

/** Hook form; while permissions are still loading every shortcut counts as open (no flash of greyed-out rows). */
export function useShortcutOpens() {
  const mine = usePermissions().data;
  const { ready, can } = useNavAccess();
  return useCallback((path: string) => !ready || shortcutOpens(mine, can, path), [ready, mine, can]);
}
